import { supabase } from '@/lib/supabase'

/**
 * Call a Supabase Edge Function and surface its actual error message.
 *
 * supabase-js returns a FunctionsHttpError for any non-2xx response, and its
 * default `.message` is a generic "Edge Function returned a non-2xx status
 * code" that hides the body we spent time writing. Read the body from the
 * response the client held on to and re-throw with the real message.
 */
export async function invokeFunction<T = unknown>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body })
  if (error) {
    let message = error.message
    const res: Response | undefined = (error as { context?: Response }).context
    if (res && typeof res.json === 'function') {
      try {
        const parsed = await res.clone().json()
        if (parsed && typeof parsed.error === 'string') message = parsed.error
      } catch {
        // Body was not JSON. Keep the generic message.
      }
    }
    throw new Error(message)
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String(data.error))
  }
  return data as T
}
